/**
 * Son API isteğinden bu yana 58 dakika geçtiyse (son 2 dakikada)
 * kullanıcıya uyarı gösterir. 60. dakikada inaktivite nedeniyle oturum kapanır.
 *
 * JWT süresi backend ile 60 dakikadır; her API yanıtında X-Auth-Token ile yenilenir.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Box,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useTranslation } from "react-i18next";
import axiosInstance from "../scenes/utils/axiosInstance";
import tenantAxiosInstance from "../scenes/utils/tenantAxiosInstance";
import superAdminAxiosInstance from "../scenes/utils/superAdminAxiosInstance";
import {
  isDoctorSessionActive,
  isTenantSessionActive,
  isSuperAdminSessionActive,
  clearAllAuthSessions,
} from "../utils/authSession";
import { API_ENDPOINTS } from "../config/api";
import { disconnectSocket } from "../socket";

import {
  updateLastActivity,
  getLastActivityTime,
  clearSessionActivity,
  SESSION_INACTIVITY_MS,
} from "../utils/sessionActivity";

const INACTIVITY_LIMIT_MS = SESSION_INACTIVITY_MS;
const WARNING_BEFORE_MS = 2 * 60 * 1000;

function getActiveSessionClient() {
  if (isSuperAdminSessionActive()) return superAdminAxiosInstance;
  if (isTenantSessionActive()) return tenantAxiosInstance;
  return axiosInstance;
}

function clearSessionAndRedirect() {
  disconnectSocket();
  clearAllAuthSessions();
  localStorage.removeItem("languageManuallySet");
  clearSessionActivity();

  const currentPath = window.location.pathname;
  if (currentPath.startsWith("/tenant/")) {
    window.location.href = "/corporate-login";
  } else if (currentPath.startsWith("/super-admin/")) {
    window.location.href = "/super-admin/auth/secure-access/login";
  } else {
    window.location.href = "/individual-login";
  }
}

export default function SessionTimeoutWarning() {
  // eslint-disable-next-line no-unused-vars
  const { i18n } = useTranslation();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_BEFORE_MS / 1000);
  const checkInterval = useRef(null);
  const countdownInterval = useRef(null);

  const hasActiveSession = () =>
    isDoctorSessionActive() || isTenantSessionActive() || isSuperAdminSessionActive();

  const startCountdown = useCallback(() => {
    setSecondsLeft(WARNING_BEFORE_MS / 1000);
    clearInterval(countdownInterval.current);
    countdownInterval.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval.current);
          clearSessionAndRedirect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleContinue = useCallback(async () => {
    try {
      const client = getActiveSessionClient();
      await client.post(API_ENDPOINTS.AUTH.REFRESH_TOKEN);
    } catch (e) {
      // 401 durumunda ilgili axios instance zaten yönlendirir
    }
    updateLastActivity();
    setShowWarning(false);
    clearInterval(countdownInterval.current);
  }, []);

  const handleLogout = useCallback(() => {
    clearInterval(countdownInterval.current);
    clearSessionAndRedirect();
  }, []);

  useEffect(() => {
    let lastLoggedActivity = Date.now();
    const handleUserActivity = () => {
      const now = Date.now();
      // Throttle: Only update every 30 seconds to avoid unnecessary localStorage writes
      if (now - lastLoggedActivity > 30000) {
        updateLastActivity();
        lastLoggedActivity = now;
      }
    };

    window.addEventListener("mousemove", handleUserActivity);
    window.addEventListener("keydown", handleUserActivity);
    window.addEventListener("click", handleUserActivity);
    window.addEventListener("scroll", handleUserActivity);

    return () => {
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("keydown", handleUserActivity);
      window.removeEventListener("click", handleUserActivity);
      window.removeEventListener("scroll", handleUserActivity);
    };
  }, []);

  useEffect(() => {
    checkInterval.current = setInterval(() => {
      if (!hasActiveSession()) return;

      const elapsed = Date.now() - getLastActivityTime();
      const timeUntilExpiry = INACTIVITY_LIMIT_MS - elapsed;

      if (timeUntilExpiry <= 0) {
        clearInterval(checkInterval.current);
        clearSessionAndRedirect();
      } else if (timeUntilExpiry <= WARNING_BEFORE_MS && !showWarning) {
        setShowWarning(true);
        startCountdown();
      }
    }, 10000);

    return () => {
      clearInterval(checkInterval.current);
      clearInterval(countdownInterval.current);
    };
  }, [showWarning, startCountdown]);

  const progressValue = (secondsLeft / (WARNING_BEFORE_MS / 1000)) * 100;

  if (!showWarning) return null;

  return (
    <Dialog
      open={showWarning}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
          border: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #f59e0b, #ef4444)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 12px",
          }}
        >
          <AccessTimeIcon sx={{ color: "#fff", fontSize: 28 }} />
        </Box>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ color: "#fff", fontSize: "1.1rem" }}
        >
          Oturum Sona Eriyor
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ textAlign: "center", pb: 1 }}>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.7)", mb: 2, lineHeight: 1.6 }}
        >
          {secondsLeft > 60
            ? `${Math.ceil(secondsLeft / 60)} dakika`
            : `${secondsLeft} saniye`}{" "}
          içinde inaktivite nedeniyle oturumunuz kapanacak.
        </Typography>

        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: "rgba(255,255,255,0.1)",
            "& .MuiLinearProgress-bar": {
              background:
                progressValue > 50
                  ? "linear-gradient(90deg, #f59e0b, #fbbf24)"
                  : "linear-gradient(90deg, #ef4444, #f87171)",
              borderRadius: 3,
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", gap: 1.5, pb: 3, px: 3 }}>
        <Button
          variant="outlined"
          onClick={handleLogout}
          sx={{
            color: "rgba(255,255,255,0.6)",
            borderColor: "rgba(255,255,255,0.2)",
            borderRadius: "10px",
            textTransform: "none",
            "&:hover": { borderColor: "rgba(255,255,255,0.4)" },
          }}
        >
          Çıkış Yap
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          sx={{
            background: "linear-gradient(135deg, #10b981, #059669)",
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            "&:hover": {
              background: "linear-gradient(135deg, #059669, #047857)",
            },
          }}
        >
          Devam Et
        </Button>
      </DialogActions>
    </Dialog>
  );
}
